import {
  METRIC_AGGREGATIONS,
  METRIC_COMPARISON_PERIODS,
  METRIC_GRANULARITIES,
  MetricAggregation,
  MetricAnalysisInput,
  MetricBaselineMethod,
  MetricComparisonPeriod,
  MetricDimensionFilterInput,
  MetricGranularity,
  MetricSeriesSpecInput,
} from "@/lib/types/metricsExplorer.ts";

export const EXPLORER_CHART_TYPES = [
  "line",
  "area",
  "stackedArea",
  "column",
  "stackedColumn",
  "scatter",
  "distribution",
  "heatmap",
] as const;
export type ExplorerChartType = (typeof EXPLORER_CHART_TYPES)[number];

/** One row of the query builder rail. */
export interface ExplorerSeriesConfig {
  ref: string;
  checkIds: number[];
  dataType: string;
  metrics: string[];
  aggregation: MetricAggregation;
  groupBy: string[];
  /**
   * Values to restrict the series to, keyed by dimension.
   *
   * A dimension mapped to an empty list is one whose dropdown is on screen with nothing
   * picked, which draws every value — the same chart as no entry at all. The two are kept
   * apart so a filter row the user opened is still there after a reload.
   */
  filters: Record<string, string[]>;
  /** Left off the chart, and therefore not queried, while staying in the builder. */
  hidden: boolean;
}

/** Which analyses to ask the server for, and with what parameters. */
export interface ExplorerAnalysisConfig {
  trend: boolean;
  baseline: boolean;
  anomaly: boolean;
  seasonality: boolean;
  movingAverage: boolean;
  movingAverageWindow: number;
  baselineMethod: MetricBaselineMethod;
  anomalySigma: number;
}

export interface ExplorerConfig {
  /** Bumped when the shape changes, so an old bookmarked link can be migrated or rejected. */
  version: number;
  series: ExplorerSeriesConfig[];
  granularity: MetricGranularity;
  comparison: MetricComparisonPeriod | null;
  chartType: ExplorerChartType;
  analysis: ExplorerAnalysisConfig;
  /**
   * Shade the periods the check was down.
   *
   * Context from Postgres rather than a computed metric, and scoped to one check, so it
   * sits outside the analysis config.
   */
  showDowntime: boolean;
}

/**
 * The single check a chart is about, or null when it spans several.
 *
 * Downtime is a property of one check; shading several checks' outages on one chart would
 * say nothing about which series each band belonged to. Hidden series are not drawn, so
 * they do not count.
 */
export const singleCheckId = (config: ExplorerConfig): number | null => {
  const ids = new Set(config.series.filter((series) => !series.hidden).flatMap((series) => series.checkIds));
  return ids.size === 1 ? [...ids][0] : null;
};

export const BASELINE_METHODS: readonly MetricBaselineMethod[] = ["WINDOW", "WINDOW_ROBUST"];

/**
 * Series one chart can hold. Each series is one spec, and the server rejects a request over
 * MAX_EXPLORER_SPECS (12); past ten, a chart stops being readable well before that.
 */
export const MAX_SERIES = 10;

export const MIN_MOVING_AVERAGE_WINDOW = 2;
export const MAX_MOVING_AVERAGE_WINDOW = 200;
export const MIN_ANOMALY_SIGMA = 0.5;
export const MAX_ANOMALY_SIGMA = 10;

export const createDefaultAnalysis = (): ExplorerAnalysisConfig => ({
  trend: false,
  baseline: false,
  anomaly: false,
  seasonality: false,
  movingAverage: false,
  movingAverageWindow: 5,
  baselineMethod: "WINDOW",
  anomalySigma: 3,
});

/** Nothing is asked for until a toggle is on, so an untouched chart costs no extra work. */
export const isAnalysisRequested = (analysis: ExplorerAnalysisConfig): boolean =>
  analysis.trend || analysis.baseline || analysis.anomaly || analysis.seasonality || analysis.movingAverage;

export const toAnalysisInput = (analysis: ExplorerAnalysisConfig): MetricAnalysisInput | null => {
  if (!isAnalysisRequested(analysis)) {
    return null;
  }
  return {
    trend: analysis.trend,
    // The bands and the outlier markers are scored against the baseline, so anomaly
    // detection implies computing it even when the readout is not on screen.
    baseline: analysis.baseline || analysis.anomaly,
    anomaly: analysis.anomaly,
    seasonality: analysis.seasonality,
    moving_average: analysis.movingAverage,
    moving_average_window: analysis.movingAverageWindow,
    baseline_method: analysis.baselineMethod,
    anomaly_sigma: analysis.anomalySigma,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

export const EXPLORER_CONFIG_VERSION = 1;

/** The single search param the whole chart definition lives in. */
export const EXPLORER_CONFIG_PARAM = "chart";

/**
 * Opens a saved chart by id instead of carrying its whole definition.
 *
 * A dashboard widget knows the view it was placed from but not its config, and linking by
 * id keeps the link short and always opens the chart as it is saved now. The page swaps it
 * for a real config in the URL as soon as it resolves, so everything downstream — sharing,
 * browser back, editing — behaves like any other explorer link.
 */
export const EXPLORER_VIEW_PARAM = "view";

let refCounter = 0;

/**
 * Refs identify a builder row across a render and in the response.
 * They only have to be unique within one config, so a counter is enough and keeps
 * generated URLs readable.
 */
export const createSeriesRef = (): string => {
  refCounter += 1;
  return `s${refCounter}`;
};

const GENERATED_REF = /^s(\d+)$/;

/**
 * Take on a ref that arrived in a link or a saved view, and move the counter past it.
 *
 * The counter is module state that restarts with the page, so a config loaded from
 * elsewhere already holds refs the counter knows nothing about. Without this, opening a
 * chart whose rows are s1..sN and adding a row hands out s1 again, and the two rows are
 * then edited and removed together.
 */
const adoptSeriesRef = (ref: string): string => {
  const generated = GENERATED_REF.exec(ref);
  if (generated) {
    refCounter = Math.max(refCounter, Number(generated[1]));
  }
  return ref;
};

/**
 * A letter per builder row, in row order: A, B, C. Hidden rows keep theirs, so hiding one
 * does not rename the lines of the others.
 */
export const seriesLetters = (config: ExplorerConfig): Record<string, string> =>
  Object.fromEntries(config.series.map((series, index) => [series.ref, String.fromCharCode(65 + index)]));

export const createEmptySeries = (): ExplorerSeriesConfig => ({
  ref: createSeriesRef(),
  checkIds: [],
  dataType: "",
  metrics: [],
  aggregation: "AVG",
  groupBy: [],
  filters: {},
  hidden: false,
});

export const createDefaultConfig = (): ExplorerConfig => ({
  version: EXPLORER_CONFIG_VERSION,
  series: [createEmptySeries()],
  granularity: "AUTO",
  comparison: null,
  chartType: "line",
  analysis: createDefaultAnalysis(),
  showDowntime: false,
});

/** A series is only worth sending once it names a check and a metric. */
export const isSeriesQueryable = (series: ExplorerSeriesConfig): boolean =>
  series.checkIds.length > 0 && series.dataType !== "" && series.metrics.length > 0;

/**
 * The specs to query: complete series that are not hidden. A hidden series is left out of
 * the request rather than queried and not drawn, so it costs nothing and the export and the
 * analysis match what is on screen.
 */
export const toQuerySpecs = (config: ExplorerConfig): MetricSeriesSpecInput[] =>
  config.series
    .filter((series) => !series.hidden && isSeriesQueryable(series))
    .map((series) => ({
      ref: series.ref,
      check_ids: series.checkIds,
      data_type: series.dataType,
      metrics: series.metrics,
      aggregation: series.aggregation,
      group_by: series.groupBy,
      ...toFilterInputs(series.filters),
    }));

/**
 * Filters for the wire, without the dimensions that have nothing picked.
 *
 * The key is left off entirely rather than sent empty, so a chart nobody has filtered
 * translates to exactly the request it did before this existed — which keeps saved charts
 * on their existing dashboard cache entries.
 */
const toFilterInputs = (filters: Record<string, string[]>): {filters?: MetricDimensionFilterInput[]} => {
  const entries = Object.entries(filters)
    .filter(([, values]) => values.length > 0)
    .map(([dimension, values]) => ({dimension, values}));
  return entries.length > 0 ? {filters: entries} : {};
};

export const serializeConfig = (config: ExplorerConfig): string => JSON.stringify(config);

/**
 * Rebuild a config from a URL param.
 *
 * Anything unreadable falls back to the default rather than throwing: a link can be
 * truncated in chat, hand-edited, or left over from an older version of the page, and
 * none of those should leave the user staring at an error boundary.
 */
export const deserializeConfig = (raw: string | null): ExplorerConfig | null => {
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const candidate = parsed as Partial<ExplorerConfig>;
  if (candidate.version !== EXPLORER_CONFIG_VERSION) {
    return null;
  }
  const takenRefs = new Set<string>();
  const series = Array.isArray(candidate.series)
    ? candidate.series.map((entry) => normalizeSeries(entry, takenRefs))
    : [];
  return {
    version: EXPLORER_CONFIG_VERSION,
    series: series.length > 0 ? series : [createEmptySeries()],
    granularity: normalizeEnum(candidate.granularity, METRIC_GRANULARITIES, "AUTO"),
    comparison: candidate.comparison
      ? normalizeEnum(candidate.comparison, METRIC_COMPARISON_PERIODS, "PREVIOUS_PERIOD")
      : null,
    chartType: normalizeEnum(candidate.chartType, EXPLORER_CHART_TYPES, "line"),
    // Optional rather than versioned: links made before analysis existed stay valid and
    // simply arrive with everything switched off.
    analysis: normalizeAnalysis(candidate.analysis),
    showDowntime: normalizeBoolean(candidate.showDowntime, false),
  };
};

const normalizeAnalysis = (raw: unknown): ExplorerAnalysisConfig => {
  const defaults = createDefaultAnalysis();
  if (typeof raw !== "object" || raw === null) {
    return defaults;
  }
  const candidate = raw as Partial<ExplorerAnalysisConfig>;
  return {
    trend: normalizeBoolean(candidate.trend, defaults.trend),
    baseline: normalizeBoolean(candidate.baseline, defaults.baseline),
    anomaly: normalizeBoolean(candidate.anomaly, defaults.anomaly),
    seasonality: normalizeBoolean(candidate.seasonality, defaults.seasonality),
    movingAverage: normalizeBoolean(candidate.movingAverage, defaults.movingAverage),
    movingAverageWindow: clamp(
      candidate.movingAverageWindow,
      MIN_MOVING_AVERAGE_WINDOW,
      MAX_MOVING_AVERAGE_WINDOW,
      defaults.movingAverageWindow
    ),
    baselineMethod: normalizeEnum(candidate.baselineMethod, BASELINE_METHODS, defaults.baselineMethod),
    anomalySigma: clamp(candidate.anomalySigma, MIN_ANOMALY_SIGMA, MAX_ANOMALY_SIGMA, defaults.anomalySigma),
  };
};

const normalizeBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

/**
 * Clamp rather than reject: the server validates these too, and a hand-edited link should
 * draw a chart with a sane window instead of failing the whole request.
 */
const clamp = (value: unknown, min: number, max: number, fallback: number): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, value));
};

const normalizeSeries = (raw: unknown, taken: Set<string>): ExplorerSeriesConfig => {
  const candidate = (typeof raw === "object" && raw !== null ? raw : {}) as Partial<ExplorerSeriesConfig>;
  // A hand-edited link can name the same ref twice, which would give two rows one identity.
  const reusable = typeof candidate.ref === "string" && candidate.ref !== "" && !taken.has(candidate.ref);
  const ref = reusable ? adoptSeriesRef(candidate.ref as string) : createSeriesRef();
  taken.add(ref);
  return {
    ref,
    checkIds: Array.isArray(candidate.checkIds) ? candidate.checkIds.filter(isPositiveInteger) : [],
    dataType: typeof candidate.dataType === "string" ? candidate.dataType : "",
    metrics: Array.isArray(candidate.metrics) ? candidate.metrics.filter(isNonEmptyString) : [],
    aggregation: normalizeEnum(candidate.aggregation, METRIC_AGGREGATIONS, "AVG"),
    groupBy: Array.isArray(candidate.groupBy) ? candidate.groupBy.filter(isNonEmptyString) : [],
    filters: normalizeFilters(candidate.filters),
    hidden: candidate.hidden === true,
  };
};

/**
 * A hand-edited link can name a dimension twice or list a number as a value, so each entry
 * is taken apart rather than trusted. An unknown dimension survives here and is dropped
 * later by ``pruneSelections``, which is the only place that knows the schema.
 */
const normalizeFilters = (raw: unknown): Record<string, string[]> => {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return {};
  }
  const result: Record<string, string[]> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([dimension, values]) => {
    if (!isNonEmptyString(dimension) || !Array.isArray(values)) {
      return;
    }
    result[dimension] = Array.from(new Set(values.filter(isNonEmptyString)));
  });
  return result;
};

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value !== "";

const normalizeEnum = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;
